import { Title } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { OmniboxErrorBoundary } from "@mirage/mosaics/Omnibox/components/OmniboxErrorBoundary";
import {
  getTimeOfDay,
  getTimeOfDayGreetingWithName,
} from "@mirage/mosaics/StartPage/Calendar/utils";
import { useIsDropboxer } from "@mirage/service-auth/useDropboxAccount";
import { useName } from "@mirage/service-auth/useName";
import {
  useFeatureFlagValue,
  useFeatureFlagValueBool,
} from "@mirage/service-experimentation/useFeatureFlagValue";
import { openURL } from "@mirage/service-platform-actions";
import { useTheme } from "@mirage/service-settings/theming/useTheme";
import { useIsMobileSize } from "@mirage/shared/responsive/mobile";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { PersistedOmnibox } from "../Omnibox/withPersistedOmniboxSurface";
import { QuerySuggestions } from "../QuerySuggestions/QuerySuggestions";
import styles from "./GreeterHeader.module.css";

const DOODLE_URLS: Record<
  "morning" | "afternoon" | "evening",
  Record<string, string>
> = {
  morning: {
    default:
      "https://assets.dropbox.com/images/dashweb/doodles/morning-doodle.png",
    dark: "https://assets.dropbox.com/images/dashweb/doodles/morning-doodle-dark.png",
  },
  afternoon: {
    default:
      "https://assets.dropbox.com/images/dashweb/doodles/afternoon-doodle.png",
    dark: "https://assets.dropbox.com/images/dashweb/doodles/afternoon-doodle-dark.png",
  },
  evening: {
    default:
      "https://assets.dropbox.com/images/dashweb/doodles/evening-doodle.png",
    dark: "https://assets.dropbox.com/images/dashweb/doodles/evening-doodle-dark.png",
  },
};

const HACKWEEK_DOODLE_URLS: Record<"default" | "dark", string> = {
  default:
    "https://assets.dropbox.com/images/dashweb/doodles/default-doodle-light.png",
  dark: "https://assets.dropbox.com/images/dashweb/doodles/default-doodle-dark.png",
};

export const GreeterHeader = () => {
  const isMobile = useIsMobileSize();
  const { t } = useTranslation();
  const isDropboxer = useIsDropboxer();
  const isDarkMode = useTheme();

  // This is redundant but adding for safety in case the greeter header gets used before April 24th
  const isOmniboxEnabled = useFeatureFlagValueBool("dash_2025_03_18_omnibox");
  const isQuerySuggestionsEnabled = useFeatureFlagValueBool(
    "dash_2025_03_24_search_and_answer_suggestions",
  );
  // TODO OSE-5791: remove Hackweek greeter once Hackweek 2025 is over
  const doodleForDashFeature = useFeatureFlagValue(
    "dash_2025_07_22_start_page_hackweek_doodle_for_dash",
  );
  const hackweekDoodleEnabled = isDropboxer && doodleForDashFeature === "ON";
  const regularDoodleEnabled = doodleForDashFeature === "V1";

  const { firstName } = useName();

  return (
    <Box
      className={cx(
        styles.greeterHeaderWrapper,
        {
          [styles.mobile]: isMobile,
        },
        "meticulous-redact-recording",
      )}
    >
      <Box className={styles.greeterHeaderContainer}>
        {hackweekDoodleEnabled && (
          <button
            className={styles.doodleButton}
            onClick={() => {
              openURL("https://app.dropboxer.net/hackdash/2025/projects/5027");
            }}
            type="button"
          >
            <img
              className={styles.doodleImage}
              src={HACKWEEK_DOODLE_URLS[isDarkMode ? "dark" : "default"]}
              alt={t("doodle_for_dash_image_alt_text")}
            />
          </button>
        )}
        {regularDoodleEnabled && (
          <img
            className={styles.doodleImage}
            src={DOODLE_URLS[getTimeOfDay()][isDarkMode ? "dark" : "default"]}
            alt={t("doodle_for_dash_alt_text")}
          />
        )}
        <Title
          weightVariant="emphasized"
          size="large"
          className={styles.titleWrapper}
        >
          {hackweekDoodleEnabled
            ? t("hackweek_doodle_for_dash_greeter", { name: firstName })
            : getTimeOfDayGreetingWithName(firstName)}
        </Title>
      </Box>
      {isOmniboxEnabled && (
        <OmniboxErrorBoundary>
          <PersistedOmnibox shouldMemoize={true} />
        </OmniboxErrorBoundary>
      )}
      {isQuerySuggestionsEnabled && <QuerySuggestions />}
    </Box>
  );
};

GreeterHeader.displayName = "GreeterHeader";
